/* eslint-disable prettier/prettier */

'use client';

import { useState } from 'react';
import type { ComponentProps, ReactElement, ReactNode } from 'react';
import { ChevronDownIcon } from 'lucide-react';
import type { DateRange, Mode } from 'react-day-picker';

import { useTranslations } from '@northladder/i18n';

import { Button } from '../button';
import { Label } from '../label';
import { Popover, PopoverContent, PopoverTrigger } from '../popover';

import { Calendar } from './calendar';

// Types
interface ICommonProps {
  buttonClassName?: string;
  calendarProps?: Omit<
    ComponentProps<typeof Calendar>,
    'selected' | 'onSelect'
  >;
  className?: string;
  footerActions?: TCalendarFooterActionsRenderer;
  formatLabel?: (
    value: Date | DateRange | Date[] | undefined,
    mode: Mode
  ) => string;
  label?: string;
  placeholder?: string;
}

interface IRangeProps extends ICommonProps {
  mode: 'range';
  onSelectDate: (v: DateRange | undefined) => void;
  selectedDate: DateRange | undefined;
}

interface ISingleProps extends ICommonProps {
  mode: 'single';
  onSelectDate: (v: Date | undefined) => void;
  selectedDate: Date | undefined;
}

interface IMultipleProps extends ICommonProps {
  mode: 'multiple';
  onSelectDate: (v: Date[] | undefined) => void;
  selectedDate: Date[] | undefined;
}

interface ICalendarFooterActionsRendererParams {
  close: () => void;
}

export type TCalendarFooterActionsRenderer = (
  params: ICalendarFooterActionsRendererParams
) => ReactNode;

export type TCalendarPickerProps = IRangeProps | ISingleProps | IMultipleProps;

/**
 * -----------------------------------------------------------------------------
 * This renders the calendar picker component for selecting a date range.
 */
export function CalendarPicker({
  buttonClassName = 'w-56 justify-between font-normal',
  calendarProps,
  className = 'flex flex-col gap-3',
  footerActions,
  formatLabel,
  label,
  mode = 'single',
  placeholder,
  ...rest
}: TCalendarPickerProps): ReactElement {
  const [open, setOpen] = useState(false);

  const t = useTranslations('DesignSystemComponents.datePicker');

  // Helpers
  const isRangeValue = (val: unknown): val is DateRange =>
    !!val &&
    typeof val === 'object' &&
    'from' in (val as Record<string, unknown>);
  const isMultipleValue = (val: unknown): val is Date[] => Array.isArray(val);

  const defaultFormat = (): string => {
    const value = rest.selectedDate as Date | DateRange | Date[] | undefined;

    if (mode === 'single') {
      return value instanceof Date
        ? value.toLocaleDateString()
        : placeholder || t('singleSelectionPlaceholder');
    }

    if (mode === 'multiple') {
      if (isMultipleValue(value) && value.length > 0) {
        const sorted = [...value].sort((a, b) => a.getTime() - b.getTime());
        if (sorted.length === 1) return sorted[0]!.toLocaleDateString();
        const first = sorted[0]!;
        const last = sorted[sorted.length - 1]!;
        return `${first.toLocaleDateString()} - ${last.toLocaleDateString()}`;
      }
      return placeholder || t('rangeSelectionPlaceholder');
    }

    // range
    if (isRangeValue(value) && value.from && value.to) {
      return `${value.from.toLocaleDateString()} - ${value.to.toLocaleDateString()}`;
    }
    return placeholder || t('rangeSelectionPlaceholder');
  };

  const displayText = formatLabel
    ? formatLabel(
      rest.selectedDate as Date | DateRange | Date[] | undefined,
      mode
    )
    : defaultFormat();

  // Build properly typed Calendar element for each mode.
  // TODO: Refactor this to use a more generic approach.
  let calendarEl: ReactElement;
  if (mode === 'single') {
    const { selectedDate, onSelectDate } = rest as ISingleProps;
    calendarEl = (
      <Calendar
        captionLayout="dropdown"
        initialFocus
        numberOfMonths={calendarProps?.numberOfMonths ?? 1}
        onSelect={onSelectDate}
        selected={selectedDate}
        {...calendarProps}
        mode="single"
      />
    );
  } else if (mode === 'multiple') {
    const { selectedDate, onSelectDate } = rest as IMultipleProps;
    calendarEl = (
      <Calendar
        captionLayout="dropdown"
        initialFocus
        numberOfMonths={calendarProps?.numberOfMonths ?? 2}
        onSelect={onSelectDate}
        selected={selectedDate}
        {...calendarProps}
        mode="multiple"
      />
    );
  } else {
    const { selectedDate, onSelectDate } = rest as IRangeProps;

    calendarEl = (
      <Calendar
        captionLayout="dropdown"
        initialFocus
        numberOfMonths={calendarProps?.numberOfMonths ?? 2}
        onSelect={onSelectDate}
        selected={selectedDate}
        {...calendarProps}
        mode="range"
      />
    );
  }

  const closePopover = () => setOpen(false);

  return (
    <div className={className}>
      {label ? (
        <Label className="px-1" htmlFor="dates">
          {label}
        </Label>
      ) : null}
      <Popover modal onOpenChange={setOpen} open={open}>
        <PopoverTrigger asChild>
          <Button className={buttonClassName} id="dates" variant="outline">
            {displayText}
            <ChevronDownIcon />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto overflow-hidden p-0">
          {calendarEl}
          {footerActions ? footerActions({ close: closePopover }) : null}
        </PopoverContent>
      </Popover>
    </div>
  );
}

export type { DateRange as TDateRange };
